/**
 * Owner-free auth provider container (ADR-001; docs/warp-drive/ADR-001-request-layer-state.md).
 * Handlers dereference providers at request time; capturing values or reader closures during
 * construction would make later provider installation stale.
 * Only the app shell installs providers. tio-data reads them without Ember imports or dependency
 * injection.
 */

export interface AuthContextProviders {
  /** Current access token, or undefined when logged out. */
  token: () => string | undefined;
  isAuthenticated: () => boolean;
  /** AuthKey#public_key sent as `x-auth-key`, when known. */
  publicKey?: () => string | undefined;
  /** Epoch ms the session expires at — drives proactive renewal. */
  sessionExpiresAt?: () => number | undefined;
  /**
   * Renewal port: the ACTION lives with the session owner; the request
   * layer only decides WHEN (threshold/cooldown bookkeeping stays in the
   * auth handler).
   */
  renew?: (token?: string) => Promise<unknown> | unknown;
  /** Observability context for error reporting (never sent on requests). */
  userId?: () => string | undefined;
}

const NO_PROVIDERS: Required<Pick<AuthContextProviders, 'token' | 'isAuthenticated'>> &
  AuthContextProviders = {
  token: () => undefined,
  isAuthenticated: () => false,
};

const current: { providers: AuthContextProviders } = { providers: NO_PROVIDERS };

export function installAuthContextProviders(providers: AuthContextProviders): void {
  current.providers = providers;
}

/** Test helper — restores the logged-out defaults. */
export function resetAuthContext(): void {
  current.providers = NO_PROVIDERS;
}

export const authContext = {
  token: (): string | undefined => current.providers.token(),
  isAuthenticated: (): boolean => current.providers.isAuthenticated(),
  publicKey: (): string | undefined => current.providers.publicKey?.(),
  sessionExpiresAt: (): number | undefined => current.providers.sessionExpiresAt?.(),
  hasRenew: (): boolean => typeof current.providers.renew === 'function',
  renew: (token?: string): Promise<unknown> | unknown => {
    const renew = current.providers.renew;
    if (!renew) {
      throw new Error('Session renewal requested but no renew provider is installed');
    }
    return renew(token);
  },
  userId: (): string | undefined => current.providers.userId?.(),
} as const;
